'use client'

import { useState, useMemo, useRef, useCallback } from 'react'
import {
  format, startOfWeek, endOfWeek, addDays, isToday, isSameDay, getHours, getMinutes, differenceInMinutes, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks, subDays
} from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@paitox/ui/lib/utils'
import {
  Plus, ChevronLeft, ChevronRight, LayoutGrid, CalendarRange, Columns, List, Clock, Activity, DollarSign, Users, CalendarDays, XCircle, GripVertical, CheckCircle, Receipt
} from 'lucide-react'
import { db, Appointment as DbAppointment } from '../lib/db'

const HOUR_H = 64
const SNAP   = 15
const DAY_START = 7

const STATUS = {
  confirmed:   { label:'Confirmada',    bg:'#dbeafe', border:'#3b82f6', text:'#1e40af' },
  pending:     { label:'Por confirmar', bg:'#fef9c3', border:'#eab308', text:'#854d0e' },
  in_progress: { label:'En progreso',   bg:'#d1fae5', border:'#10b981', text:'#065f46' },
  completed:   { label:'Completada',    bg:'#f0fdf4', border:'#86efac', text:'#166534' },
  cancelled:   { label:'Cancelada',     bg:'#fee2e2', border:'#f87171', text:'#991b1b' },
}

type CalView = 'month' | 'week' | 'day' | 'list'

const fmtTime = (d: Date) => format(d, 'HH:mm')
const fmtPrice = (n: number) => `$${(n / 1000).toFixed(0)}K`

function getAptsForDay(apts: DbAppointment[], day: Date) {
  return apts.filter(a => isSameDay(new Date(a.start), day))
}
function aptTop(apt: DbAppointment) {
  const startDate = new Date(apt.start)
  return (getHours(startDate) - DAY_START + getMinutes(startDate) / 60) * HOUR_H
}
function aptHeight(apt: DbAppointment) {
  return Math.max((differenceInMinutes(new Date(apt.end), new Date(apt.start)) / 60) * HOUR_H, 28)
}
function snapMinutes(raw: number) {
  return Math.round(raw / SNAP) * SNAP
}

function KpiStrip({ apts }: { apts: DbAppointment[] }) {
  const todayApts = getAptsForDay(apts, new Date())
  const revenue = todayApts.reduce((s, a) => s + a.price, 0)
  const kpis = [
    { label:'Citas hoy',  value:String(todayApts.length),          sub:`${todayApts.filter(a=>a.status==='confirmed').length} confirmadas`, icon:CalendarDays, c:'text-blue-600 bg-blue-50' },
    { label:'Ingresos',   value:fmtPrice(revenue),                  sub:'CLP facturado hoy',   icon:DollarSign,  c:'text-cyan-600 bg-cyan-50' },
    { label:'En sala',    value:String(todayApts.filter(a=>a.status==='in_progress').length), sub:'clientes activos', icon:Users, c:'text-violet-600 bg-violet-50' },
    { label:'Ocupación',  value:'78%',                              sub:'promedio salas',       icon:Activity,    c:'text-rose-600 bg-rose-50' },
  ]
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
      {kpis.map(k => (
        <div key={k.label} className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-4 flex items-center gap-3">
          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center shrink-0', k.c)}>
            <k.icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-slate-500 font-medium leading-none mb-1">{k.label}</p>
            <p className="text-xl font-bold text-slate-800 leading-none">{k.value}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">{k.sub}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

function EventPill({
  apt, onClick, onDragStart,
}: {
  apt: DbAppointment
  onClick: (a: DbAppointment) => void
  onDragStart: (e: React.DragEvent, aptId: string) => void
}) {
  const s = STATUS[apt.status] || STATUS.pending
  const h = aptHeight(apt)

  return (
    <div
      draggable
      onDragStart={e => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        onDragStart(e, apt.id)
        e.dataTransfer.setData('apt-offset-y', String(e.clientY - rect.top))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onClick={e => { e.stopPropagation(); onClick(apt) }}
      className="absolute left-1 right-1 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing select-none hover:brightness-95 transition-all group"
      style={{
        top: aptTop(apt),
        height: h,
        background: s.bg,
        borderLeft: `3px solid ${s.border}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
        zIndex: 10,
      }}
    >
      <div className="px-1.5 py-1 h-full flex flex-col justify-start overflow-hidden">
        <div className="flex items-center gap-1">
          <GripVertical className="h-2.5 w-2.5 shrink-0 opacity-30 group-hover:opacity-70 transition-opacity" style={{ color: s.border }} />
          <span className="font-semibold leading-tight truncate" style={{ color: s.text, fontSize:'0.7rem' }}>
            {apt.clientName}
          </span>
        </div>
        {h > 42 && (
          <span className="text-[10px] leading-tight truncate pl-3.5 opacity-80" style={{ color: s.text }}>
            {apt.service}
          </span>
        )}
        {h > 58 && (
          <span className="text-[10px] leading-none pl-3.5 opacity-60" style={{ color: s.border }}>
            {fmtTime(new Date(apt.start))} – {fmtTime(new Date(apt.end))}
          </span>
        )}
      </div>
    </div>
  )
}

const HOURS = Array.from({ length: 15 }, (_, i) => i + DAY_START)

function TimeColumn({
  day, apts, onEventClick, onDragStart, onDropInColumn,
}: {
  day: Date
  apts: DbAppointment[]
  onEventClick: (a: DbAppointment) => void
  onDragStart: (e: React.DragEvent, aptId: string) => void
  onDropInColumn: (day: Date, y: number, offsetY: number) => void
}) {
  const dayApts = getAptsForDay(apts, day)
  const [dragOver, setDragOver] = useState(false)
  const colRef = useRef<HTMLDivElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOver(true)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const rect = colRef.current!.getBoundingClientRect()
    const rawY = e.clientY - rect.top
    const offsetY = parseFloat(e.dataTransfer.getData('apt-offset-y') || '0')
    onDropInColumn(day, rawY, offsetY)
  }

  return (
    <div
      ref={colRef}
      className={cn(
        'flex-1 relative border-l border-slate-100 min-w-0 transition-colors',
        dragOver && 'bg-cyan-50/40'
      )}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {HOURS.map(h => (
        <div key={h} style={{ height: HOUR_H }} className="border-b border-slate-100/80 relative">
          <div className="absolute left-0 right-0 border-b border-dashed border-slate-100/60" style={{ top: HOUR_H / 2 }} />
        </div>
      ))}
      {dayApts.map(apt => (
        <EventPill key={apt.id} apt={apt} onClick={onEventClick} onDragStart={onDragStart} />
      ))}
    </div>
  )
}

function WeekView({ baseDate, apts, onEventClick, onDragStart, onDropInGrid }: {
  baseDate: Date; apts: DbAppointment[]
  onEventClick: (a: DbAppointment) => void
  onDragStart: (e: React.DragEvent, aptId: string) => void
  onDropInGrid: (day: Date, y: number, offsetY: number) => void
}) {
  const weekStart = startOfWeek(baseDate, { weekStartsOn: 1 })
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto">
      <div className="flex shrink-0 border-b border-slate-200 bg-slate-50/80 sticky top-0 z-10">
        <div className="w-14 shrink-0" />
        {days.map(day => (
          <div key={day.toISOString()} className="flex-1 py-2 text-center border-l border-slate-100">
            <div className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">
              {format(day, 'EEE', { locale: es })}
            </div>
            <div className={cn(
              'mx-auto mt-0.5 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold',
              isToday(day) ? 'bg-cyan-600 text-white' : 'text-slate-700'
            )}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-1">
        <div className="w-14 shrink-0 border-r border-slate-100">
          {HOURS.map(h => (
            <div key={h} style={{ height: HOUR_H }} className="relative">
              <span className="absolute -top-2 right-2 text-[10px] text-slate-400 font-medium select-none">{h}:00</span>
            </div>
          ))}
        </div>
        <div className="flex flex-1 min-w-0">
          {days.map(day => (
            <TimeColumn key={day.toISOString()} day={day} apts={apts}
              onEventClick={onEventClick} onDragStart={onDragStart} onDropInColumn={onDropInGrid} />
          ))}
        </div>
      </div>
    </div>
  )
}

function DayView({ baseDate, apts, onEventClick, onDragStart, onDropInGrid }: {
  baseDate: Date; apts: DbAppointment[]
  onEventClick: (a: DbAppointment) => void
  onDragStart: (e: React.DragEvent, aptId: string) => void
  onDropInGrid: (day: Date, y: number, offsetY: number) => void
}) {
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto">
      <div className="flex shrink-0 border-b border-slate-200 bg-slate-50/80 sticky top-0 z-10">
        <div className="w-14 shrink-0" />
        <div className="flex-1 py-2 text-center border-l border-slate-100">
          <div className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">
            {format(baseDate, 'EEEE', { locale: es })}
          </div>
          <div className={cn('mx-auto mt-0.5 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold', isToday(baseDate) ? 'bg-cyan-600 text-white' : 'text-slate-700')}>
            {format(baseDate, 'd')}
          </div>
        </div>
      </div>
      <div className="flex flex-1">
        <div className="w-14 shrink-0 border-r border-slate-100">
          {HOURS.map(h => (
            <div key={h} style={{ height: HOUR_H }} className="relative">
              <span className="absolute -top-2 right-2 text-[10px] text-slate-400 font-medium select-none">{h}:00</span>
            </div>
          ))}
        </div>
        <TimeColumn day={baseDate} apts={apts} onEventClick={onEventClick} onDragStart={onDragStart} onDropInColumn={onDropInGrid} />
      </div>
    </div>
  )
}

function MonthView({ baseDate, apts, onEventClick, onDragStart, onDropInDay, onDayClick }: {
  baseDate: Date; apts: DbAppointment[]
  onEventClick: (a: DbAppointment) => void
  onDragStart: (e: React.DragEvent, aptId: string) => void
  onDropInDay: (day: Date) => void
  onDayClick: (d: Date) => void
}) {
  const gridStart = startOfWeek(startOfMonth(baseDate), { weekStartsOn: 1 })
  const gridEnd   = endOfWeek(endOfMonth(baseDate),   { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
  const weekDays = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom']
  const [dragOverDay, setDragOverDay] = useState<string | null>(null)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/80 shrink-0">
        {weekDays.map(d => (
          <div key={d} className="py-2 text-center text-[10px] font-semibold uppercase text-slate-400 tracking-wider">{d}</div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-7 overflow-auto" style={{ gridAutoRows: 'minmax(80px,1fr)' }}>
        {days.map((day: Date) => {
          const key = day.toISOString()
          const dayApts = getAptsForDay(apts, day).sort((a,b) => new Date(a.start).getTime() - new Date(b.start).getTime())
          const _today = isToday(day)
          const inMonth = day.getMonth() === baseDate.getMonth()
          const isOver = dragOverDay === key

          return (
            <div
              key={key}
              onClick={() => onDayClick(day)}
              onDragOver={e => { e.preventDefault(); setDragOverDay(key) }}
              onDragLeave={() => setDragOverDay(null)}
              onDrop={e => { e.preventDefault(); setDragOverDay(null); onDropInDay(day) }}
              className={cn(
                'border-r border-b border-slate-100 p-1.5 cursor-pointer transition-colors',
                !inMonth && 'bg-slate-50/60',
                _today && 'bg-cyan-50/30',
                isOver && 'bg-cyan-50 ring-1 ring-cyan-400/30 ring-inset',
              )}
            >
              <div className={cn(
                'h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold mb-1',
                _today ? 'bg-cyan-600 text-white' : inMonth ? 'text-slate-700' : 'text-slate-300'
              )}>
                {format(day, 'd')}
              </div>
              <div className="space-y-0.5">
                {dayApts.slice(0, 3).map(apt => {
                  const s = STATUS[apt.status] || STATUS.pending
                  return (
                    <div
                      key={apt.id}
                      draggable
                      onDragStart={e => { e.stopPropagation(); onDragStart(e, apt.id) }}
                      onClick={e => { e.stopPropagation(); onEventClick(apt) }}
                      className="rounded px-1 py-0.5 text-[10px] font-medium truncate cursor-grab active:cursor-grabbing hover:brightness-95 flex items-center gap-1"
                      style={{ background: s.bg, color: s.text, borderLeft: `2px solid ${s.border}` }}
                    >
                      <span>{fmtTime(new Date(apt.start))}</span>
                      <span className="truncate">{apt.clientName.split(' ')[0]}</span>
                    </div>
                  )
                })}
                {dayApts.length > 3 && (
                  <div className="text-[10px] text-cyan-600 font-semibold pl-1">+{dayApts.length-3} más</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListView({ baseDate, apts, onSelect }: { baseDate: Date; apts: DbAppointment[]; onSelect: (a: DbAppointment) => void }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(baseDate, { weekStartsOn: 1 }), i))
  return (
    <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
      {days.map(day => {
        const dayApts = getAptsForDay(apts, day).sort((a,b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        if (!dayApts.length) return null
        return (
          <div key={day.toISOString()}>
            <div className="flex items-center gap-2 mb-2">
              <div className={cn('h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                isToday(day) ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600')}>
                {format(day, 'd')}
              </div>
              <span className="text-sm font-semibold text-slate-700 capitalize">
                {format(day, "EEEE, d 'de' MMMM", { locale: es })}
              </span>
            </div>
            <div className="space-y-2 ml-9">
              {dayApts.map(apt => {
                const s = STATUS[apt.status] || STATUS.pending
                return (
                  <div key={apt.id} onClick={() => onSelect(apt)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200/70 cursor-pointer hover:shadow-md transition-all">
                    <div className="h-10 w-1 rounded-full shrink-0" style={{ background: s.border }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-800 truncate">{apt.clientName}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>{s.label}</span>
                      </div>
                      <span className="text-xs text-slate-500 truncate">{apt.service} · {apt.room}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-semibold text-slate-600">{fmtTime(new Date(apt.start))} – {fmtTime(new Date(apt.end))}</div>
                      <div className="text-xs text-slate-400">{apt.staff}</div>
                    </div>
                    <div className="text-sm font-bold text-slate-700 shrink-0">{fmtPrice(apt.price)}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function AptModal({ apt, onClose, onUpdateStatus }: { apt: DbAppointment; onClose: () => void; onUpdateStatus: (id: string, st: any) => void }) {
  const s = STATUS[apt.status] || STATUS.pending
  const [confirmDte, setConfirmDte] = useState(false)
  const [confirmCode, setConfirmCode] = useState('')
  const [dteStatus, setDteStatus] = useState<'none' | 'success' | 'error'>('none')

  const handleEmitDte = () => {
    if (confirmCode === '1234') {
      setDteStatus('success')
      const notifications = db.getNotifications()
      const newNotif = {
        id: `not-${Date.now()}`,
        text: `Boleta LibreDTE SII emitida con éxito para ${apt.clientName} (Monto: $${apt.price.toLocaleString('es-CL')})`,
        time: 'Hace un momento',
        unread: true
      }
      db.saveNotifications([newNotif, ...notifications])
    } else {
      setDteStatus('error')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="h-1.5 rounded-t-2xl" style={{ background: s.border }} />
        <div className="p-5 overflow-y-auto max-h-[90vh]">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>{s.label}</span>
              <h3 className="text-base font-bold text-slate-800 mt-2">{apt.clientName}</h3>
              <p className="text-sm text-slate-500">{apt.phone}</p>
            </div>
            <button onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400">
              <XCircle className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-2.5 text-sm">
            {[
              { icon: <CalendarDays className="h-4 w-4 text-cyan-500" />, text: format(new Date(apt.start),"EEEE d 'de' MMMM",{locale:es}) },
              { icon: <Clock className="h-4 w-4 text-blue-500" />, text: `${fmtTime(new Date(apt.start))} – ${fmtTime(new Date(apt.end))} (${differenceInMinutes(new Date(apt.end), new Date(apt.start))} min)` },
              { icon: <Activity className="h-4 w-4 text-violet-500" />, text: apt.service },
              { icon: <Users className="h-4 w-4 text-slate-400" />, text: `${apt.staff} · ${apt.room}` },
              { icon: <DollarSign className="h-4 w-4 text-emerald-500" />, text: `$${apt.price.toLocaleString('es-CL')} CLP` },
            ].map((r,i) => (
              <div key={i} className="flex items-center gap-2.5"><div className="shrink-0">{r.icon}</div><span className="text-slate-700">{r.text}</span></div>
            ))}
          </div>

          {apt.status === 'completed' && (
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Facturación SII (LibreDTE)</span>

              {dteStatus === 'success' ? (
                <div className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-800 flex items-center gap-1.5 font-semibold">
                  <CheckCircle className="h-4 w-4 text-emerald-600" /> Boleta SII exenta emitida con éxito.
                </div>
              ) : (
                <div className="space-y-2">
                  {!confirmDte ? (
                    <button onClick={() => setConfirmDte(true)}
                      className="w-full h-8 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5">
                      <Receipt className="h-4 w-4 text-cyan-400" /> Emitir Boleta Manual (SII)
                    </button>
                  ) : (
                    <div className="space-y-2 border border-slate-100 p-2.5 rounded-lg bg-slate-50">
                      <label className="text-[10px] font-bold text-slate-500 block">Código de Confirmación (Pon: 1234)</label>
                      <div className="flex gap-1.5">
                        <input type="password" placeholder="Código..." className="flex-1 h-8 border border-slate-200 rounded px-2 text-xs" value={confirmCode} onChange={e => setConfirmCode(e.target.value)} />
                        <button onClick={handleEmitDte} className="px-3 h-8 bg-cyan-600 text-white rounded text-xs font-bold">Emitir</button>
                      </div>
                      {dteStatus === 'error' && <p className="text-[10px] text-rose-600 font-bold">Código incorrecto. Reintente.</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {apt.notes && (
            <div className="mt-3 p-2.5 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-600">
              <span className="font-semibold block mb-0.5 text-[10px] uppercase text-slate-400">Notas:</span>
              {apt.notes}
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-slate-100">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Cambiar estado</label>
            <div className="grid grid-cols-3 gap-1.5">
              {(['confirmed', 'in_progress', 'completed', 'cancelled'] as const).map(st => (
                <button
                  key={st}
                  onClick={() => onUpdateStatus(apt.id, st)}
                  className={cn(
                    "px-2 py-1 text-[10px] font-semibold rounded-lg border transition capitalize",
                    apt.status === st ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {st === 'confirmed' ? 'Confirmado' : st === 'in_progress' ? 'En Sala' : st === 'completed' ? 'Completado' : 'Cancelado'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={onClose} className="flex-1 h-9 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function NewAptModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const clients = useMemo(() => db.getClients(), [])
  const services = useMemo(() => db.getServices(), [])
  const rooms = useMemo(() => db.getRooms(), [])
  const staff = useMemo(() => db.getStaff(), [])

  const [clientId, setClientId] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [roomId, setRoomId] = useState('')
  const [staffId, setStaffId] = useState('')
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [time, setTime] = useState('10:00')
  const [notes, setNotes] = useState('')

  const handleCreate = () => {
    if (!clientId || !serviceId) return

    const cl = clients.find(c => c.id === clientId)
    const srv = services.find(s => s.id === serviceId)
    const rm = rooms.find(r => r.id === roomId) || rooms[0]
    const stf = staff.find(s => s.id === staffId) || staff[0]

    if (!cl || !srv) return

    const [h, m] = time.split(':').map(Number)
    const [y, mo, d] = date.split('-').map(Number)
    const startStr = new Date(y, mo - 1, d, h, m).toISOString()
    const endStr = new Date(new Date(startStr).getTime() + srv.durationMin * 60 * 1000).toISOString()

    const newApt: DbAppointment = {
      id: `apt-${Date.now()}`,
      clientId,
      clientName: `${cl.firstName} ${cl.lastName}`,
      serviceId,
      service: srv.name,
      roomId: rm.id,
      room: rm.name,
      staffId: stf.id,
      staff: stf.name,
      start: startStr,
      end: endStr,
      status: 'confirmed',
      price: srv.price,
      phone: cl.phone,
      notes
    }

    const current = db.getAppointments()
    db.saveAppointments([...current, newApt])

    const payments = db.getPayments()
    db.savePayments([...payments, {
      id: `pay-${Date.now()}`,
      clientId: cl.id,
      clientName: `${cl.firstName} ${cl.lastName}`,
      type: 'appointment',
      referenceId: newApt.id,
      referenceName: srv.name,
      amount: srv.price,
      method: 'CASH',
      status: 'PAID',
      createdAt: new Date().toISOString()
    }])

    onCreated()
  }

  const inp = 'w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition-all'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">Nueva cita</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XCircle className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Cliente *</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className={inp}>
              <option value="">Seleccionar cliente...</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Servicio *</label>
            <select value={serviceId} onChange={e => setServiceId(e.target.value)} className={inp}>
              <option value="">Seleccionar servicio...</option>
              {services.map(s=><option key={s.id} value={s.id}>{s.name} – {s.durationMin} min</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Sala</label>
              <select value={roomId} onChange={e => setRoomId(e.target.value)} className={inp}>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Especialista</label>
              <select value={staffId} onChange={e => setStaffId(e.target.value)} className={inp}>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Fecha</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inp} />
            </div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Hora</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} className={inp} />
            </div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Notas</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className={inp + ' h-auto resize-none py-2'} rows={2} placeholder="Indicaciones especiales..." />
          </div>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
          <button onClick={handleCreate} disabled={!clientId || !serviceId} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Crear cita</button>
        </div>
      </div>
    </div>
  )
}

type CalOpts = { value: CalView; label: string; icon: React.FC<{ className?: string }> }

const VIEW_OPTS: CalOpts[] = [
  { value:'month', label:'Mes',    icon:LayoutGrid },
  { value:'week',  label:'Semana', icon:CalendarRange },
  { value:'day',   label:'Día',    icon:Columns },
  { value:'list',  label:'Lista',  icon:List },
]

export function CalendarPage() {
  const [view, setView]           = useState<CalView>('week')
  const [baseDate, setBaseDate]   = useState(new Date())
  const [apts, setApts]           = useState<DbAppointment[]>(() => db.getAppointments())
  const [selectedApt, setSelectedApt] = useState<DbAppointment | null>(null)
  const [showNew, setShowNew]     = useState(false)
  const [dragId, setDragId]       = useState<string | null>(null)

  const reloadApts = () => {
    setApts(db.getAppointments())
  }

  const handleDragStart = useCallback((e: React.DragEvent, aptId: string) => {
    setDragId(aptId)
    e.dataTransfer.setData('apt-id', aptId)
  }, [])

  const handleDropInGrid = useCallback((day: Date, rawY: number, offsetY: number) => {
    const id = dragId
    setDragId(null)
    if (!id) return

    const apt = apts.find(a => a.id === id)
    if (!apt) return

    const adjustedY = Math.max(0, rawY - offsetY)
    const totalMinutes = (adjustedY / HOUR_H) * 60
    const snappedMinutes = snapMinutes(totalMinutes)
    const newStartHour = Math.floor(snappedMinutes / 60) + DAY_START
    const newStartMin  = snappedMinutes % 60

    const clampedHour = Math.max(DAY_START, Math.min(20, newStartHour))
    const duration = differenceInMinutes(new Date(apt.end), new Date(apt.start))

    const newStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), clampedHour, newStartMin)
    const newEnd   = new Date(newStart.getTime() + duration * 60000)

    const updated = apts.map(a => a.id === id ? { ...a, start: newStart.toISOString(), end: newEnd.toISOString() } : a)
    setApts(updated)
    db.saveAppointments(updated)
  }, [dragId, apts])

  const handleDropInDay = useCallback((day: Date) => {
    const id = dragId
    setDragId(null)
    if (!id) return

    const apt = apts.find(a => a.id === id)
    if (!apt) return

    const duration = differenceInMinutes(new Date(apt.end), new Date(apt.start))
    const sDate = new Date(apt.start)
    const newStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), getHours(sDate), getMinutes(sDate))
    const newEnd   = new Date(newStart.getTime() + duration * 60000)

    const updated = apts.map(a => a.id === id ? { ...a, start: newStart.toISOString(), end: newEnd.toISOString() } : a)
    setApts(updated)
    db.saveAppointments(updated)
  }, [dragId, apts])

  const nav = (dir: 'prev' | 'next') => {
    const d = dir === 'next' ? 1 : -1
    setBaseDate(bd =>
      view === 'month' ? (d > 0 ? addMonths(bd, 1) : subMonths(bd, 1))
      : view === 'day'  ? (d > 0 ? addDays(bd, 1)   : subDays(bd, 1))
      : (d > 0 ? addWeeks(bd, 1) : subWeeks(bd, 1))
    )
  }

  const updateStatus = (id: string, st: any) => {
    const updated = apts.map(a => a.id === id ? { ...a, status: st } : a)
    setApts(updated)
    db.saveAppointments(updated)
    setSelectedApt(null)
  }

  const title = useMemo(() => {
    if (view === 'month') return format(baseDate,'MMMM yyyy',{locale:es})
    if (view === 'day')   return format(baseDate,"EEEE, d 'de' MMMM",{locale:es})
    const ws = startOfWeek(baseDate,{weekStartsOn:1})
    const we = endOfWeek(baseDate,{weekStartsOn:1})
    return `${format(ws,'d MMM',{locale:es})} – ${format(we,'d MMM yyyy',{locale:es})}`
  },[view,baseDate])

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">

      <KpiStrip apts={apts} />

      <div className="flex-1 bg-white rounded-2xl border border-slate-200/70 shadow-sm flex flex-col overflow-hidden min-h-0">

        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 shrink-0 flex-wrap">
          <button onClick={() => nav('prev')} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => setBaseDate(new Date())} className="h-8 px-3 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-cyan-600 transition">
            Hoy
          </button>
          <button onClick={() => nav('next')} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition">
            <ChevronRight className="h-4 w-4" />
          </button>

          <h2 className="flex-1 text-sm font-semibold text-slate-700 capitalize pl-1 select-none truncate">{title}</h2>

          {dragId && (
            <div className="text-[11px] text-cyan-600 font-medium animate-pulse hidden sm:block">
              Arrastrando cita…
            </div>
          )}

          <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
            {VIEW_OPTS.map(({ value, label, icon: Icon }) => (
              <button key={value} onClick={() => setView(value)}
                className={cn(
                  'flex items-center gap-1.5 px-3 h-7 text-xs font-semibold rounded-md transition-all',
                  view === value ? 'bg-white text-cyan-700 shadow-sm border border-slate-200/60' : 'text-slate-500 hover:text-slate-700'
                )}>
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Nueva cita</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {view === 'week' && (
            <WeekView baseDate={baseDate} apts={apts}
              onEventClick={setSelectedApt} onDragStart={handleDragStart} onDropInGrid={handleDropInGrid} />
          )}
          {view === 'day' && (
            <DayView baseDate={baseDate} apts={apts}
              onEventClick={setSelectedApt} onDragStart={handleDragStart} onDropInGrid={handleDropInGrid} />
          )}
          {view === 'month' && (
            <MonthView baseDate={baseDate} apts={apts}
              onEventClick={setSelectedApt} onDragStart={handleDragStart} onDropInDay={handleDropInDay}
              onDayClick={d => { setBaseDate(d); setView('day') }} />
          )}
          {view === 'list' && (
            <ListView baseDate={baseDate} apts={apts} onSelect={setSelectedApt} />
          )}
        </div>
      </div>

      {selectedApt && <AptModal apt={selectedApt} onClose={() => setSelectedApt(null)} onUpdateStatus={updateStatus} />}
      {showNew && <NewAptModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reloadApts() }} />}
    </div>
  )
}
